import { useState } from 'react';
import { Link } from 'react-router';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { formatDate } from '../lib/format';
import { Icon } from './Icon';

/**
 * MCP personal-access-token manager — mint a per-user bearer for the hosted MCP,
 * list your own tokens, revoke. The raw token is shown ONCE at creation (server
 * stores only its hash). Tokens are scoped to your staff identity; revoking (or
 * expiry) cuts that token's MCP access.
 */
const TOKENS = gql`
  query McpTokens {
    mcpTokens {
      id
      tokenPrefix
      label
      createdAt
      lastUsedAt
      expiresAt
      revoked
    }
  }
`;

const CREATE = gql`
  mutation CreateMcpToken($label: String) {
    createMcpToken(label: $label) {
      id
      token
      tokenPrefix
      expiresAt
    }
  }
`;

const REVOKE = gql`
  mutation RevokeMcpToken($id: String!) {
    revokeMcpToken(id: $id)
  }
`;

interface Token {
  id: string;
  tokenPrefix: string;
  label?: string;
  createdAt: string;
  lastUsedAt?: string;
  expiresAt?: string;
  revoked: boolean;
}

export function McpTokens() {
  const { data, loading, refetch } = useQuery<{ mcpTokens: Token[] }>(TOKENS, {
    fetchPolicy: 'cache-and-network',
  });
  const [label, setLabel] = useState('');
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [create, { loading: creating }] = useMutation<{ createMcpToken: { token: string } }>(CREATE, {
    onCompleted: (d) => {
      setFreshToken(d.createMcpToken.token);
      setCopied(false);
      setLabel('');
      refetch();
    },
  });
  const [revoke] = useMutation(REVOKE, { onCompleted: () => refetch() });

  const tokens = data?.mcpTokens ?? [];

  return (
    <div>
      <h3 className="panel-title">Personal access tokens</h3>
      <p className="detail-desc">
        A personal access token authenticates you with the hosted MCP server. Mint one here and
        paste it into your AI client config as the Bearer token — the <Link to="/docs/mcp">MCP</Link>{' '}
        page has the connection snippet. Tokens are tied to your account — revoke one here and its
        MCP access stops immediately. The token is shown <strong>once</strong>; we store only a hash.
      </p>

      <div className="dep-card mcp-token-mint" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="label (e.g. laptop, claude desktop)"
          className="mcp-token-label-input"
        />
        <button
          type="button"
          className="btn btn-primary"
          disabled={creating}
          onClick={() => create({ variables: { label: label || null } })}
        >
          {creating ? 'Creating…' : 'Create token'}
        </button>
      </div>

      {freshToken && (
        <div className="dep-card" style={{ borderColor: 'var(--accent, #4a9)', marginTop: 8 }}>
          <p className="muted text-xs" style={{ marginBottom: 6 }}>
            <Icon name="alert" /> Copy this now — it won't be shown again.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <code className="text-sm" style={{ flex: 1, wordBreak: 'break-all' }}>{freshToken}</code>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                navigator.clipboard?.writeText(freshToken);
                setCopied(true);
              }}
            >
              {copied ? <><Icon name="check" /> Copied</> : 'Copy'}
            </button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setFreshToken(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {loading && !tokens.length ? (
        <p className="muted">Loading…</p>
      ) : tokens.length === 0 ? (
        <p className="muted text-sm">No tokens yet.</p>
      ) : (
        <div className="docs-table-scroll" role="region" aria-label="Personal access tokens" tabIndex={0}><table>
          <thead>
            <tr>
              <th>Token</th>
              <th>Label</th>
              <th>Created</th>
              <th>Last used</th>
              <th>Expires</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id} style={t.revoked ? { opacity: 0.5 } : undefined}>
                <td><code>{t.tokenPrefix}…</code></td>
                <td className="muted">{t.label ?? '—'}</td>
                <td className="muted">{formatDate(t.createdAt)}</td>
                <td className="muted">{formatDate(t.lastUsedAt)}</td>
                <td className="muted">{formatDate(t.expiresAt)}</td>
                <td>
                  {t.revoked ? (
                    <span className="muted">revoked</span>
                  ) : (
                    <span className="mtx-yes">active</span>
                  )}
                </td>
                <td>
                  {!t.revoked && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => {
                        if (confirm(`Revoke token ${t.tokenPrefix}…? This is immediate.`)) {
                          revoke({ variables: { id: t.id } });
                        }
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </div>
  );
}
